import React from 'react';
import { motion } from 'framer-motion';
import { ComposedChart, Line, Bar, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Zap, Settings, AlertCircle, CheckCircle2, TrendingUp, Battery } from 'lucide-react';
// Mock Data
const generationData = Array.from({
  length: 24
}, (_, i) => ({
  time: `${i}:00`,
  actual: i > 6 && i < 20 ? Math.sin((i - 6) / 14 * Math.PI) * 100 * (0.8 + Math.random() * 0.2) : 0,
  forecast: i > 6 && i < 20 ? Math.sin((i - 6) / 14 * Math.PI) * 100 : 0,
  availability: 98
}));
const assetStatus = [{
  name: 'Online',
  value: 12,
  color: '#10b981'
}, {
  name: 'Maintenance',
  value: 2,
  color: '#f59e0b'
}, {
  name: 'Fault',
  value: 1,
  color: '#ef4444'
}];
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white/95 backdrop-blur-xl border border-slate-200 rounded-xl p-3 shadow-xl z-50" data-id="element-732">
        <p className="text-xs font-bold text-slate-900 mb-1" data-id="element-733">{label}</p>
        {payload.map((entry: any) => <div key={entry.name} className="flex items-center gap-2 text-xs" data-id="element-734">
            <div className="w-2 h-2 rounded-full" style={{
          backgroundColor: entry.color
        }} data-id="element-735" />
            <span className="text-slate-600 capitalize" data-id="element-736">{entry.name}:</span>
            <span className="font-mono font-medium" data-id="element-737">
              {entry.value.toFixed(1)} MW
            </span>
          </div>)}
      </div>;
  }
  return null;
};
export function AssetPerformanceDashboard() {
  return <div className="min-h-screen p-6 space-y-6 pb-20 bg-violet-50/30" data-id="element-738">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-739">
        <div data-id="element-740">
          <div className="flex items-center gap-2 mb-1" data-id="element-741">
            <div className="p-2 bg-violet-500 rounded-lg" data-id="element-742">
              <Zap size={20} className="text-white" data-id="element-743" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" data-id="element-744">
              Asset Performance
            </h1>
          </div>
          <p className="text-slate-500 text-sm md:text-base ml-11" data-id="element-745">
            Real-time monitoring of generation assets and availability
          </p>
        </div>

        <div className="flex gap-4" data-id="element-746">
          <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-slate-200 shadow-sm" data-id="element-747">
            <CheckCircle2 size={16} className="text-emerald-500" data-id="element-748" />
            <span className="text-sm font-bold text-slate-700" data-id="element-749">
              94.5% Availability
            </span>
          </div>
          <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-slate-200 shadow-sm" data-id="element-750">
            <TrendingUp size={16} className="text-violet-500" data-id="element-751" />
            <span className="text-sm font-bold text-slate-700" data-id="element-752">
              142 MW Output
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-753">
        {/* Generation vs Forecast */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="lg:col-span-2 bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-754">
          <div className="flex justify-between items-center mb-6" data-id="element-755">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-756">
              Generation vs Forecast
            </h3>
            <div className="flex gap-2 text-xs" data-id="element-757">
              <span className="flex items-center gap-1" data-id="element-758">
                <div className="w-2 h-2 rounded-full bg-violet-500" data-id="element-759"></div>{' '}
                Actual
              </span>
              <span className="flex items-center gap-1" data-id="element-760">
                <div className="w-2 h-2 rounded-full bg-slate-400" data-id="element-761"></div>{' '}
                Forecast
              </span>
            </div>
          </div>

          <div className="h-[350px] w-full" data-id="element-762">
            <ResponsiveContainer width="100%" height="100%" data-id="element-763">
              <ComposedChart data={generationData} margin={{
              top: 10,
              right: 10,
              left: 0,
              bottom: 0
            }} data-id="element-764">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-765" />
                <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#64748b'
              }} interval={3} data-id="element-766" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#64748b'
              }} unit=" MW" data-id="element-767" />
                <Tooltip content={<CustomTooltip data-id="element-769" />} data-id="element-768" />
                <Area type="monotone" dataKey="actual" fill="#8b5cf6" stroke="#8b5cf6" fillOpacity={0.2} data-id="element-770" />
                <Line type="monotone" dataKey="forecast" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" dot={false} data-id="element-771" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Fleet Status */}
        <div className="space-y-6" data-id="element-772">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-773">
            <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-774">
              Fleet Status
            </h3>
            <div className="h-[200px] w-full relative" data-id="element-775">
              <ResponsiveContainer width="100%" height="100%" data-id="element-776">
                <PieChart data-id="element-777">
                  <Pie data={assetStatus} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" data-id="element-778">
                    {assetStatus.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-779" />)}
                  </Pie>
                  <Tooltip data-id="element-780" />
                  <Legend verticalAlign="bottom" height={36} data-id="element-781" />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10" data-id="element-782">
                <div className="text-center pb-8" data-id="element-783">
                  <span className="block text-3xl font-bold text-slate-900" data-id="element-784">
                    15
                  </span>
                  <span className="text-xs text-slate-500" data-id="element-785">Total Assets</span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-786">
            <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-787">Alerts</h3>
            <div className="space-y-3" data-id="element-788">
              <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-100" data-id="element-789">
                <AlertCircle size={16} className="text-red-600 mt-0.5" data-id="element-790" />
                <div data-id="element-791">
                  <p className="text-sm font-bold text-red-700" data-id="element-792">
                    Inverter Fault - Solar 04
                  </p>
                  <p className="text-xs text-red-600" data-id="element-793">
                    Efficiency drop detected (-15%)
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100" data-id="element-794">
                <Settings size={16} className="text-amber-600 mt-0.5" data-id="element-795" />
                <div data-id="element-796">
                  <p className="text-sm font-bold text-amber-700" data-id="element-797">
                    Maintenance Due - Wind 02
                  </p>
                  <p className="text-xs text-amber-600" data-id="element-798">
                    Scheduled for tomorrow 08:00
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}